import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { grcService } from '../../services/grcService';
import { authService } from '../../services/authService';
import { storageService } from '../../services/storageService';
import { Framework, GRCControl, DashboardData } from '../../types/grc';

// Import all functional components
import { UnifiedRiskRegister } from './UnifiedRiskRegister';
import { UnifiedAssetInventory } from './UnifiedAssetInventory';
import { FrameworkSoA } from './FrameworkSoA';
import { SupplierManagement } from './SupplierManagement';
import { PolicyGenerator } from '../dashboard/PolicyGenerator';
import { AuditManager } from '../dashboard/AuditManager';
import { IncidentManager } from '../dashboard/IncidentManager';
import { AnalyticsManager } from '../dashboard/AnalyticsManager';
import { FrameworkUploader } from './FrameworkUploader';
import { ComplianceWizard } from './ComplianceWizard';
import { FrameworkDashboard } from './FrameworkDashboard';
import { GRCAnalytics } from './GRCAnalytics';
import { FrameworkQuestionnaire } from './FrameworkQuestionnaire';
import { SettingsModal } from '../dashboard/SettingsModal';
import { UnifiedAIAssistant } from './UnifiedAIAssistant';
import { ConfirmModal } from '../ui/ConfirmModal';

// Enterprise module imports
import {
  EnterpriseTraining,
  EnterpriseAutomation,
  EnterpriseCloud,
  EnterpriseSaaS,
  EnterpriseEndpoints,
  EnterprisePentest,
  EnterpriseTrustCenter,
  EnterpriseExecutive
} from '../enterprise';

import {
  LayoutDashboard, FileText, Shield, AlertTriangle, Database,
  Building2, ClipboardList, BarChart2, Settings, Wand2, GraduationCap,
  Zap, Cloud, Layers, Monitor, Bug, BadgeCheck, TrendingUp as Executive,
  ChevronRight, ChevronLeft, Check, X, AlertCircle, Clock,
  Users, Filter, Search, Plus, Upload, AlertOctagon,
  Brain, Mic, Send, TrendingUp, RefreshCw, ExternalLink,
  CheckCircle, XCircle, HelpCircle, Globe, Menu, Sun, Moon,
  PanelLeftClose, PanelLeft, Trash2, LogOut, UserCircle
} from 'lucide-react';

// Sidebar navigation items with permission requirements and groupings
const NAV_GROUPS = [
  {
    id: 'main',
    label: null, // No label for main group
    items: [
      { key: 'overview', icon: LayoutDashboard, labelKey: 'overview', section: 'overview', requiresAdmin: false },
    ]
  },
  {
    id: 'compliance',
    label: 'Compliance',
    labelSr: 'Usklađenost',
    items: [
      { key: 'controls', icon: Shield, labelKey: 'controls', section: 'controls', requiresAdmin: false },
      { key: 'risks', icon: AlertTriangle, labelKey: 'risks', section: 'risks', requiresAdmin: false },
      { key: 'assets', icon: Database, labelKey: 'assets', section: 'assets', requiresAdmin: false },
      { key: 'policies', icon: Wand2, labelKey: 'policies', section: 'policies', requiresAdmin: false },
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    labelSr: 'Operacije',
    items: [
      { key: 'audits', icon: ClipboardList, labelKey: 'audits', section: 'audits', requiresAdmin: false },
      { key: 'incidents', icon: AlertOctagon, labelKey: 'incidents', section: 'incidents', requiresAdmin: false },
      { key: 'suppliers', icon: Building2, labelKey: 'suppliers', section: 'suppliers', requiresAdmin: false },
    ]
  },
  {
    id: 'insights',
    label: 'Insights',
    labelSr: 'Uvidi',
    items: [
      { key: 'analytics', icon: BarChart2, labelKey: 'analytics', section: 'analytics', requiresAdmin: false },
      { key: 'upload', icon: Upload, labelKey: 'programs', section: 'programs', requiresAdmin: false },
    ]
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    labelSr: 'Enterprise',
    items: [
      { key: 'training', icon: GraduationCap, labelKey: 'training', section: 'training', requiresAdmin: false },
      { key: 'automation', icon: Zap, labelKey: 'automation', section: 'automation', requiresAdmin: false },
      { key: 'cloud', icon: Cloud, labelKey: 'cloud', section: 'cloud', requiresAdmin: false },
      { key: 'saas', icon: Layers, labelKey: 'saas', section: 'saas', requiresAdmin: false },
      { key: 'endpoints', icon: Monitor, labelKey: 'endpoints', section: 'endpoints', requiresAdmin: false },
      { key: 'pentest', icon: Bug, labelKey: 'pentest', section: 'pentest', requiresAdmin: false },
      { key: 'trust-center', icon: BadgeCheck, labelKey: 'trustCenter', section: 'trust-center', requiresAdmin: false },
      { key: 'executive', icon: Executive, labelKey: 'executive', section: 'executive', requiresAdmin: false },
    ]
  }
];

// Flatten for backwards compatibility
const NAV_ITEMS = NAV_GROUPS.flatMap(g => g.items);

// Main unified dashboard
export const UnifiedGRCDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language, switchLanguage } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const toast = useToast();
  
  // Sidebar state
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  
  // Navigation state - read initial value from URL
  const [activeSection, setActiveSectionState] = useState<string>(() => 
    searchParams.get('section') || 'overview'
  );
  
  // Framework state
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [selectedFramework, setSelectedFrameworkState] = useState<Framework | null>(null);
  const [controls, setControls] = useState<GRCControl[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Track if URL framework was applied
  const [urlFrameworkApplied, setUrlFrameworkApplied] = useState(false);
  
  // User state
  const [userRole, setUserRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [userName, setUserName] = useState<string>('User');
  
  // Modal states
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  
  // Delete confirmation modal state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [frameworkToDelete, setFrameworkToDelete] = useState<Framework | null>(null);
  
  // Filters for navigation
  const [filters, setFilters] = useState<any>({});
  
  // AI state
  const [aiQuery, setAiQuery] = useState('');

  // Wrapper to sync activeSection with URL
  const setActiveSection = (section: string) => {
    setActiveSectionState(section);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('section', section);
    setSearchParams(newParams, { replace: true });
  };

  // Wrapper to sync selectedFramework with URL
  const setSelectedFramework = (fw: Framework | null) => {
    setSelectedFrameworkState(fw);
    const newParams = new URLSearchParams(searchParams);
    if (fw) {
      newParams.set('fw', String(fw.id));
    } else {
      newParams.delete('fw');
    }
    setSearchParams(newParams, { replace: true });
  };

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Reload controls when framework changes
  useEffect(() => {
    if (selectedFramework) {
      loadFrameworkData(selectedFramework.id);
    }
  }, [selectedFramework?.id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load user
      const user = await authService.getCurrentUser();
      if (user) {
        setUserRole(user.role);
        setPermissions(user.permissions || {});
        setUserName(user.username?.split('@')[0] || 'User');
      }
      
      // Load frameworks
      const frameworkList = await grcService.getFrameworksWithStatus();
      setFrameworks(frameworkList);
      
      // Check URL for framework ID first, then fallback to ISO27001 or first
      if (frameworkList.length > 0 && !urlFrameworkApplied) {
        const urlFwId = searchParams.get('fw');
        let initialFramework: Framework | undefined;
        
        if (urlFwId) {
          // Try to find framework from URL
          initialFramework = frameworkList.find(f => f.id === parseInt(urlFwId, 10));
        }
        
        if (!initialFramework) {
          // Fallback to ISO 27001 or first
          const isoFramework = frameworkList.find(f => f.code?.includes('27001'));
          initialFramework = isoFramework || frameworkList[0];
        }
        
        setSelectedFrameworkState(initialFramework);
        setUrlFrameworkApplied(true);
        
        // Check if wizard needed for initial framework
        if (!initialFramework.wizard_completed) {
          setShowWizard(true);
        }
        
        // Update URL if it didn't have the fw param
        if (!urlFwId) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('fw', String(initialFramework.id));
          setSearchParams(newParams, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFrameworkData = async (frameworkId: number) => {
    try {
      const [dashboard, soaData] = await Promise.all([
        grcService.getFrameworkDashboard(frameworkId),
        grcService.getSoA(frameworkId)
      ]);
      setDashboardData(dashboard);
      setControls(soaData);
    } catch (error) {
      console.error('Error loading framework data:', error);
    }
  };

  const handleFrameworkSelect = (fw: Framework) => {
    setSelectedFramework(fw);
    // Check if wizard needed
    if (!fw.wizard_completed) {
      setShowWizard(true);
    }
  };

  const handleWizardComplete = async () => {
    setShowWizard(false);
    await loadInitialData();
  };

  const handleUploadComplete = async (frameworkId: number) => {
    await loadInitialData();
    const newFw = frameworks.find(f => f.id === frameworkId);
    if (newFw) {
      setSelectedFramework(newFw);
      setActiveSection('overview');
      setShowWizard(true);
    }
  };

  const [deletingFrameworkId, setDeletingFrameworkId] = useState<number | null>(null);

  // Open delete confirmation modal
  const handleDeleteFrameworkClick = (e: React.MouseEvent, fw: Framework) => {
    e.stopPropagation(); // Don't trigger card click
    setFrameworkToDelete(fw);
    setDeleteConfirmOpen(true);
  };

  // Execute delete after confirmation
  const handleDeleteFrameworkConfirm = async () => {
    if (!frameworkToDelete) return;
    
    const fw = frameworkToDelete;
    setDeletingFrameworkId(fw.id);
    
    try {
      const ok = await grcService.deleteFramework(fw.id);
      if (ok) {
        // If deleting the currently selected framework, deselect it
        if (selectedFramework?.id === fw.id) {
          setSelectedFramework(null);
          setDashboardData(null);
          setControls([]);
        }
        // Remove from local state without reloading all data
        setFrameworks(prev => prev.filter(f => f.id !== fw.id));
        
        toast.success(
          language === 'sr' ? 'Framework obrisan' : 'Framework deleted',
          language === 'sr' 
            ? `${fw.code} je uspešno obrisan.`
            : `${fw.code} has been successfully deleted.`
        );
      } else {
        toast.error(
          language === 'sr' ? 'Greška' : 'Error',
          language === 'sr' ? 'Greška pri brisanju framework-a.' : 'Error deleting framework.'
        );
      }
    } catch (error) {
      console.error('Delete framework error:', error);
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Greška pri brisanju framework-a.' : 'Error deleting framework.'
      );
    } finally {
      setDeletingFrameworkId(null);
      setDeleteConfirmOpen(false);
      setFrameworkToDelete(null);
    }
  };

  const handleQuestionnaireComplete = async (answers: Record<string, string | string[]>, applicableControls: string[]) => {
    // Apply questionnaire results - update controls applicability
    console.log('Questionnaire completed:', { answers, applicableControls });
    // In real implementation, this would call API to update controls
    await loadInitialData();
  };

  const handleNavigate = (tab: string, newFilters?: any) => {
    setActiveSection(tab);
    if (newFilters) {
      setFilters(newFilters);
    }
  };

  const hasPermission = (perm: string) => {
    if (userRole === 'admin') return true;
    if (Object.keys(permissions).length > 0) {
      return !!permissions[perm];
    }
    return true;
  };

  // Control statistics
  const controlStats = useMemo(() => {
    const critical = controls.filter(c => c.status === 'non_compliant' && c.severity === 'critical').length;
    const atRisk = controls.filter(c => c.status === 'partial').length;
    const healthy = controls.filter(c => c.status === 'compliant').length;
    const notSet = controls.filter(c => c.status === 'not_assessed').length;
    
    return { critical, atRisk, healthy, notSet, total: controls.length };
  }, [controls]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t.dashboard.welcome}...</p>
        </div>
      </div>
    );
  }

  // Don't replace the whole dashboard with wizard - it will be shown as a modal overlay

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Sidebar - Fixed position */}
      <aside className={`
        ${sidebarExpanded ? 'w-56' : 'w-16'} 
        bg-slate-900 dark:bg-gray-950 flex flex-col border-r border-slate-800 transition-all duration-300 
        fixed inset-y-0 left-0 z-40
      `}>
        {/* Logo & Toggle */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <button
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            title={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <img src="/logo.svg" alt="Imperion GRC" className="w-10 h-10 flex-shrink-0" />
            {sidebarExpanded && (
              <span className="text-white font-semibold text-lg">Imperion</span>
            )}
          </button>
        </div>
        
        {/* Navigation - grouped with sections */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto scrollbar-thin">
          <div className="space-y-4">
            {NAV_GROUPS.map((group, groupIdx) => {
              const visibleItems = group.items.filter(item => {
                if (authService.isGlobalAdmin() || authService.isOrgAdmin()) return true;
                return authService.hasPermission(item.section, 'view');
              });
              
              if (visibleItems.length === 0) return null;
              
              return (
                <div key={group.id}>
                  {/* Group label */}
                  {group.label && sidebarExpanded && (
                    <div className="px-2 mb-2 flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        {language === 'sr' ? group.labelSr : group.label}
                      </span>
                      <div className="flex-1 h-px bg-gradient-to-r from-slate-700 to-transparent" />
                    </div>
                  )}
                  {/* Divider for collapsed state */}
                  {group.label && !sidebarExpanded && groupIdx > 0 && (
                    <div className="mx-2 mb-2 h-px bg-slate-700" />
                  )}
                  
                  {/* Group items */}
                  <div className="space-y-0.5">
                    {visibleItems.map(item => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.key;
                      const label = t.nav[item.labelKey as keyof typeof t.nav] || item.key;
                      
                      return (
                        <button
                          key={item.key}
                          onClick={() => setActiveSection(item.key)}
                          className={`
                            group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 text-[13px]
                            ${isActive 
                              ? 'bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 border-l-2 border-orange-500 shadow-sm shadow-orange-500/10' 
                              : 'text-slate-400 hover:text-white hover:bg-slate-800/70 border-l-2 border-transparent'
                            }
                          `}
                          title={!sidebarExpanded ? label : undefined}
                        >
                          <div className={`
                            p-1 rounded-md transition-all duration-200
                            ${isActive 
                              ? 'bg-orange-500/20' 
                              : 'group-hover:bg-slate-700'
                            }
                          `}>
                            <Icon className="w-4 h-4 flex-shrink-0" />
                          </div>
                          {sidebarExpanded && (
                            <span className="font-medium truncate">{label}</span>
                          )}
                          {isActive && sidebarExpanded && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </nav>
      </aside>

      {/* Main content - offset for fixed sidebar */}
      <main className={`flex-1 flex flex-col min-h-screen overflow-hidden ${sidebarExpanded ? 'ml-56' : 'ml-16'} transition-all duration-300`}>
        {/* Top bar */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t.nav[activeSection as keyof typeof t.nav] || t.nav.overview}
            </h1>
            
            {/* Framework selector */}
            <div className="relative">
              <select
                value={selectedFramework?.id || ''}
                onChange={(e) => {
                  const fw = frameworks.find(f => f.id === Number(e.target.value));
                  if (fw) handleFrameworkSelect(fw);
                }}
                className="pl-4 pr-10 py-2 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
              >
                {frameworks.map(fw => (
                  <option key={fw.id} value={fw.id}>
                    {fw.code} - {fw.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Framework stats */}
            {selectedFramework && (
              <div className="flex items-center gap-3 text-sm">
                <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg font-medium">
                  {Number(dashboardData?.status?.applicable_controls ?? selectedFramework.applicable_controls) || 0} {t.dashboard.applicable}
                </span>
                <span className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium">
                  {Number(dashboardData?.stats?.total ?? selectedFramework.total_controls) || 0} {t.dashboard.totalControls}
                </span>
              </div>
            )}
            
            {/* Questionnaire button */}
            {selectedFramework && (
              <button
                onClick={() => setIsQuestionnaireOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-sm font-medium hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                {t.questionnaire.title}
              </button>
            )}
            
            {/* User dropdown with account actions */}
            <div className="relative">
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{userName}</span>
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${userDropdownOpen ? 'rotate-90' : ''}`} />
              </button>
              
              {userDropdownOpen && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div className="fixed inset-0 z-40" onClick={() => setUserDropdownOpen(false)} />
                  
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1.5 z-50">
                    {/* User info */}
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{userName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{userRole}</p>
                    </div>
                    
                    {/* Language toggle */}
                    <button
                      onClick={() => { switchLanguage(); setUserDropdownOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      <span>{language === 'sr' ? 'Switch to English' : 'Prebaci na srpski'}</span>
                    </button>
                    
                    {/* Theme toggle */}
                    <button
                      onClick={() => { toggleTheme(); setUserDropdownOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                      <span>{t.settings.theme}</span>
                    </button>
                    
                    {/* Settings */}
                    <button
                      onClick={() => { setIsSettingsOpen(true); setUserDropdownOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span>{t.settings.title}</span>
                    </button>
                    
                    <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                    
                    {/* Logout */}
                    <button
                      onClick={() => authService.logout()}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{language === 'sr' ? 'Odjavi se' : 'Logout'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content area */}
        <div className="flex-1 overflow-auto p-6">
          {/* Overview Section - Active Frameworks Overview when no framework selected */}
          {activeSection === 'overview' && !selectedFramework && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {t.analytics.activeFrameworks}
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">
                    {language === 'sr' 
                      ? 'Pregled svih aktivnih regulativa i standarda'
                      : 'Overview of all active regulations and standards'
                    }
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {frameworks.map(fw => {
                  // Calculate a simple applicability percentage as proxy for setup completeness
                  const setupScore = fw.applicable_controls > 0 
                    ? Math.round(((fw.applicable_controls) / (fw.total_controls || 1)) * 100) 
                    : 0;
                  const healthColor = fw.wizard_completed 
                    ? (setupScore >= 50 ? 'emerald' : 'amber') 
                    : 'red';
                  
                  return (
                    <button
                      key={fw.id}
                      onClick={() => {
                        handleFrameworkSelect(fw);
                      }}
                      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 text-left hover:shadow-lg transition-all hover:border-indigo-400 dark:hover:border-indigo-600"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                          {fw.framework_type}
                        </span>
                        <div className={`w-3 h-3 rounded-full ${
                          healthColor === 'emerald' ? 'bg-emerald-500' : 
                          healthColor === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                        }`} title={`${setupScore}% ${t.dashboard.applicable}`} />
                      </div>
                      
                      <h4 className="font-semibold text-gray-900 dark:text-white text-lg mb-1">{fw.code}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4">{fw.name}</p>
                      
                      {/* Applicability Progress Bar */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500 dark:text-gray-400">{t.dashboard.applicable}</span>
                          <span className={`font-semibold ${
                            healthColor === 'emerald' ? 'text-emerald-600' : 
                            healthColor === 'amber' ? 'text-amber-600' : 'text-red-600'
                          }`}>{fw.applicable_controls}/{fw.total_controls}</span>
                        </div>
                        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              healthColor === 'emerald' ? 'bg-emerald-500' : 
                              healthColor === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                            } rounded-full transition-all duration-500`}
                            style={{ width: `${setupScore}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="mt-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{fw.total_controls || 0} {t.controls.title.toLowerCase()}</span>
                        <div className="flex items-center gap-2">
                          {fw.wizard_completed ? (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="w-3.5 h-3.5" />
                              {language === 'sr' ? 'Konfigurisano' : 'Configured'}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-amber-600">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              {language === 'sr' ? 'Potrebna konfiguracija' : 'Setup needed'}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {frameworks.length === 0 && (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {language === 'sr' 
                      ? 'Još nema aktivnih okvira. Dodajte prvi okvir da biste započeli.'
                      : 'No active frameworks yet. Add your first framework to get started.'
                    }
                  </p>
                  <button
                    onClick={() => setActiveSection('upload')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    {t.programs.addNew}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Overview Section - Framework Dashboard when framework selected */}
          {activeSection === 'overview' && selectedFramework && (
            <FrameworkDashboard
              framework={selectedFramework}
              onResetWizard={() => setShowWizard(true)}
              onNavigate={handleNavigate}
            />
          )}

          {/* Controls / SoA Section */}
          {activeSection === 'controls' && selectedFramework && hasPermission('view_soa') && (
            <FrameworkSoA
              frameworkId={selectedFramework.id}
              framework={selectedFramework}
              userRole={userRole}
              onNavigate={handleNavigate}
            />
          )}

          {/* Risks Section */}
          {activeSection === 'risks' && hasPermission('view_risks') && (
            <UnifiedRiskRegister userRole={userRole} />
          )}

          {/* Assets Section */}
          {activeSection === 'assets' && hasPermission('view_assets') && (
            <UnifiedAssetInventory userRole={userRole} />
          )}

          {/* Policies Section */}
          {activeSection === 'policies' && selectedFramework && hasPermission('view_policies') && (
            <PolicyGenerator
              userRole={userRole}
              initialSearchQuery={filters.searchQuery}
              onNavigate={handleNavigate}
              frameworkId={selectedFramework.id}
              framework={selectedFramework}
            />
          )}

          {/* Audits Section */}
          {activeSection === 'audits' && selectedFramework && hasPermission('view_audit') && (
            <AuditManager
              onNavigate={handleNavigate}
              frameworkId={selectedFramework.id}
              framework={selectedFramework}
            />
          )}

          {/* Incidents Section */}
          {activeSection === 'incidents' && hasPermission('view_incidents') && (
            <IncidentManager
              userRole={userRole}
              initialFilters={filters}
            />
          )}

          {/* Suppliers Section */}
          {activeSection === 'suppliers' && (
            <SupplierManagement userRole={userRole} />
          )}

          {/* Analytics Section */}
          {activeSection === 'analytics' && hasPermission('view_analytics') && (
            <GRCAnalytics 
              onNavigate={handleNavigate}
              selectedFramework={selectedFramework}
              onFrameworkSelect={(fw) => {
                setSelectedFramework(fw);
                setActiveSection('overview');
              }}
            />
          )}

          {/* Upload Section */}
          {activeSection === 'upload' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {t.programs.addNew}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {language === 'sr' 
                    ? 'Otpremite novi standard, regulativu ili benchmark u PDF formatu. AI će automatski obraditi dokument i ekstrahovati kontrole.'
                    : 'Upload a new standard, regulation, or benchmark in PDF format. AI will automatically process the document and extract controls.'
                  }
                </p>
                <FrameworkUploader onUploadComplete={handleUploadComplete} onCancel={() => setActiveSection('overview')} />
              </div>
              
              {/* Existing frameworks list */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {language === 'sr' ? 'Postojeći okviri' : 'Existing Frameworks'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {frameworks.map(fw => (
                    <div
                      key={fw.id}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all hover:shadow-lg group ${
                        selectedFramework?.id === fw.id
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                      }`}
                    >
                      {/* Delete button */}
                      <button
                        onClick={(e) => handleDeleteFrameworkClick(e, fw)}
                        disabled={deletingFrameworkId === fw.id}
                        className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-500/0 hover:bg-red-500/10 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all z-10"
                        title={language === 'sr' ? 'Obriši framework' : 'Delete framework'}
                      >
                        {deletingFrameworkId === fw.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                      {/* Clickable card body */}
                      <button
                        onClick={() => {
                          handleFrameworkSelect(fw);
                          setActiveSection('overview');
                        }}
                        className="w-full text-left"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                            {fw.framework_type}
                          </span>
                          <div className="flex items-center gap-1">
                            {fw.wizard_completed ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <span className="text-xs text-amber-500 font-medium">0</span>
                            )}
                          </div>
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{fw.code}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{fw.name}</p>
                        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                          <span>{fw.total_controls || 0} {t.controls.title}</span>
                          {fw.applicable_controls > 0 && (
                            <>
                              <span>•</span>
                              <span className="text-emerald-600">{fw.applicable_controls} {t.dashboard.applicable}</span>
                            </>
                          )}
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Enterprise Modules */}
          {activeSection === 'training' && (
            <EnterpriseTraining />
          )}

          {activeSection === 'automation' && (
            <EnterpriseAutomation />
          )}

          {activeSection === 'cloud' && (
            <EnterpriseCloud frameworkName={selectedFramework?.name} />
          )}

          {activeSection === 'saas' && (
            <EnterpriseSaaS frameworkName={selectedFramework?.name} />
          )}

          {activeSection === 'endpoints' && (
            <EnterpriseEndpoints frameworkName={selectedFramework?.name} />
          )}

          {activeSection === 'pentest' && (
            <EnterprisePentest frameworkName={selectedFramework?.name} />
          )}

          {activeSection === 'trust-center' && (
            <EnterpriseTrustCenter />
          )}

          {activeSection === 'executive' && (
            <EnterpriseExecutive />
          )}
        </div>
      </main>

      {/* Modals */}
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} isGlobalAdmin={authService.isGlobalAdmin()} />
      
      {selectedFramework && (
        <FrameworkQuestionnaire
          framework={selectedFramework}
          controls={controls}
          isOpen={isQuestionnaireOpen}
          onClose={() => setIsQuestionnaireOpen(false)}
          onComplete={handleQuestionnaireComplete}
        />
      )}

      {/* Compliance Wizard Modal Overlay */}
      {showWizard && selectedFramework && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto m-4">
            <ComplianceWizard
              framework={selectedFramework}
              onComplete={handleWizardComplete}
              onCancel={() => setShowWizard(false)}
            />
          </div>
        </div>
      )}

      {/* Floating AI Assistant — available on all pages */}
      <UnifiedAIAssistant
        framework={selectedFramework}
        isFloating={true}
      />

      {/* Delete Framework Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setFrameworkToDelete(null);
        }}
        onConfirm={handleDeleteFrameworkConfirm}
        title={language === 'sr' ? 'Potvrdi brisanje' : 'Confirm Delete'}
        message={
          frameworkToDelete
            ? (language === 'sr'
                ? `Da li ste sigurni da želite da obrišete "${frameworkToDelete.code} — ${frameworkToDelete.name}"?\n\nSvi podaci (kontrole, rizici, auditi, dokazi, itd.) će biti trajno obrisani!\n\nOva akcija se NE MOŽE poništiti.`
                : `Are you sure you want to delete "${frameworkToDelete.code} — ${frameworkToDelete.name}"?\n\nAll data (controls, risks, audits, evidence, etc.) will be permanently deleted!\n\nThis action CANNOT be undone.`)
            : ''
        }
        confirmText={language === 'sr' ? 'Obriši' : 'Delete'}
        cancelText={language === 'sr' ? 'Otkaži' : 'Cancel'}
        type="danger"
        isLoading={deletingFrameworkId !== null}
      />
    </div>
  );
};

export default UnifiedGRCDashboard;
